import { PlusIcon } from '@phosphor-icons/react';
import { Button } from 'components/button/Button';
import { Formik } from 'components/form/formik';
import { updateOrderLineContent } from '../../models';
import diff from 'object-diff';
import { useMemo } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { invariant } from '@utils/invariant';
import * as Yup from 'yup';

import { ISimpleComboboxItem } from '../../../../components/combobox/SimpleCombobox';
import { InputField } from '../../../../components/input/InputField';
import { getDisplayError } from '../../../../utils/get-display-error';
import { decimateAndFormatNumber, parseNumberInput } from '../../../../utils/number';
import { CargoTypeComboboxField, cargoTypeToComboboxItem } from '../../cargoType/CargoTypeComboboxField';
import { PackageTypeComboboxField, packageTypeToComboboxItem } from '../../packageType/PackageTypeComboboxField';
import { useTranslation } from '../../../../contexts/translation-context';
import { useOrderLine } from '../orderLine/orderLineContext';
import { OrderLineContent } from './OrderLineContents';
import { captureException } from '@sentry/react';

const createOrderLineContentSchema = Yup.object().shape({
  cargoType: Yup.mixed().nullable().required('Vereist'),
  packages: Yup.number().required('Vereist'),
  packageType: Yup.mixed().nullable().required('Vereist'),
  weight: Yup.number().required('Vereist'),
  volume: Yup.number().required('Vereist'),
  loadingMeters: Yup.number().required('Vereist'),
});

interface IOrderLineContentValues {
  cargoType: ISimpleComboboxItem | null;
  packages: string;
  packageType: ISimpleComboboxItem | null;
  weight: string;
  volume: string;
  loadingMeters: string;
}

interface IPageContentProps {
  orderLineContent: OrderLineContent;
}

const PageContent: React.FC<IPageContentProps> = (props) => {
  const { orderLineContent } = props;

  const navigate = useNavigate();
  const { lang } = useTranslation();
  const { refreshData } = useOrderLine();

  const initialValues: IOrderLineContentValues = useMemo(() => {
    return {
      cargoType: cargoTypeToComboboxItem(orderLineContent.cargoType, lang),
      packages: decimateAndFormatNumber(orderLineContent.packages, 2),
      packageType: packageTypeToComboboxItem(orderLineContent.packageType, lang),
      weight: decimateAndFormatNumber(orderLineContent.weight, 2),
      volume: decimateAndFormatNumber(orderLineContent.volume, 2),
      loadingMeters: decimateAndFormatNumber(orderLineContent.loadingMeters, 2),
    };
  }, [orderLineContent, lang]);

  return (
    <>
      <h1 className="heading-one mb-4">Pas inhoud aan</h1>

      <Formik
        initialValues={initialValues}
        validationSchema={createOrderLineContentSchema}
        onSubmit={async (values) => {
          try {
            const patch: Partial<typeof values> = diff(initialValues, values);
            const { cargoType, packageType, packages, weight, volume, loadingMeters, ...otherValues } = patch;
            const cargoTypeId = cargoType?.key;
            const packageTypeId = packageType?.key;
            await updateOrderLineContent(orderLineContent.id, {
              cargoTypeId,
              packageTypeId,
              packages: packages != null ? parseNumberInput(packages, 2) : undefined,
              weight: weight != null ? parseNumberInput(weight, 2) : undefined,
              volume: volume != null ? parseNumberInput(volume, 2) : undefined,
              loadingMeters: loadingMeters != null ? parseNumberInput(loadingMeters, 2) : undefined,
              ...otherValues,
            });
            await refreshData();
            navigate('..');
            toast.success('Inhoud aangepast');
          } catch (err: any) {
            captureException(err);
            toast.error('Kon inhoud niet aanpassen: ' + getDisplayError(err));
          }
        }}
      >
        {({ handleSubmit, isSubmitting }) => {
          return (
            <form onSubmit={handleSubmit}>
              <CargoTypeComboboxField labelText="Inhoud" name="cargoType" />
              <div className="flex gap-2">
                <InputField labelText="Coli" type="number" name="packages" step="0.01" />
                <PackageTypeComboboxField labelText="Verpakking" name="packageType" />
              </div>
              <div className="flex gap-2">
                <InputField labelText="Gewicht (kg)" type="number" name="weight" step="0.01" />
                <InputField labelText="Volume (m3)" type="number" name="volume" step="0.01" />
                <InputField labelText="Laadmeter" type="number" name="loadingMeters" step="0.01" />
              </div>

              <Button
                type="submit"
                color="primary"
                iconLeft={<PlusIcon className="button-icon" />}
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
              >
                Pas inhoud aan
              </Button>
            </form>
          );
        }}
      </Formik>
    </>
  );
};

export const UpdateOrderLineContentPage = () => {
  const { orderLine } = useOrderLine();
  const { orderLineContentId: _orderLineContentId } = useParams<{ orderLineContentId: string }>();
  invariant(_orderLineContentId);
  const orderLineContentId = +_orderLineContentId;

  const orderLineContent = useMemo(() => {
    return orderLine.contents.find((c) => c.id === orderLineContentId);
  }, [orderLine, orderLineContentId]);

  if (!orderLineContent) {
    return <div>Inhoud niet gevonden</div>;
  }

  return <PageContent orderLineContent={orderLineContent} />;
};
